import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do cleaning companies get more leads in Colorado?",
    a: "The fastest path is a tuned Google Business Profile + dedicated landing pages for each service (residential recurring, move-out, post-construction, commercial janitorial). 'House cleaners near me' is one of the highest-intent searches in any Colorado market, and most cleaning sites convert poorly on mobile.",
  },
  {
    q: "What's the best marketing channel for a Colorado cleaning business?",
    a: "Local SEO + Google reviews is the highest-ROI channel — cleaning buyers read 10+ reviews and trust matters more than price. Google Ads works well for move-out and post-construction (high-ticket, urgent). Recurring residential is built on SEO and referrals.",
  },
  {
    q: "Should commercial janitorial have its own page?",
    a: "Yes. Commercial janitorial buyers (property managers, office tenants) search very differently than residential. A dedicated page with case studies, square-footage pricing guides, and after-hours scheduling info converts far better than one generic 'cleaning services' page.",
  },
  {
    q: "How long does cleaning company SEO take to work?",
    a: "Most Colorado cleaning companies see Map Pack movement in 60–120 days. Residential terms ('house cleaning Colorado Springs') are competitive but lower than HVAC/plumbing. Commercial and niche services (post-construction, Airbnb turnover) rank much faster.",
  },
  {
    q: "Can you help with Airbnb / short-term rental turnover marketing?",
    a: "Yes — and it's one of the most-missed niches. Dedicated landing pages for STR turnover in mountain markets (Manitou Springs, Woodland Park) convert dramatically better than generic residential pages.",
  },
];

export const Route = createFileRoute("/cleaning-marketing-colorado")({
  head: () =>
    industryMarketingHead("Cleaning Company", "cleaning company", "cleaning-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Cleaning Company"
      industryLower="cleaning company"
      slug="cleaning-marketing-colorado"
      subheadline="We help Colorado cleaning companies fill recurring residential, move-outs, post-construction, and commercial janitorial routes."
      painPoints={[
        "Recurring residential clients churn faster than new ones come in",
        "Commercial janitorial bids never get found by property managers searching online",
        "Move-out and post-construction calls go to the cleaner that ranks first on Google Maps",
      ]}
      serviceDescriptions={{
        seo: "Rank for residential, move-out, post-construction, Airbnb turnover, and commercial janitorial across every Colorado market.",
        googleMaps: "Dominate the local Map Pack so 'house cleaning near me' calls come to you first.",
        webDesign: "Fast, mobile-first cleaning sites with instant-quote forms and recurring-plan upsells.",
        ppc: "Profitable Google Ads tuned to move-out, post-construction, and high-ticket commercial bids.",
        geoAi: "Get cited when Coloradans ask ChatGPT 'best house cleaner near me' or 'how much does deep cleaning cost'.",
        socialMedia: "Before-and-after content that builds trust for high-end residential and STR turnover.",
      }}
      testimonial={{
        quote:
          "We doubled our recurring residential clients in six months and finally have a steady stream of commercial bids coming through the site.",
        role: "Cleaning Company Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Cleaning marketing in Colorado Springs", description: "Help Springs cleaners win recurring residential and commercial routes across Briargate, Northgate, and downtown." },
        { to: "/denver-digital-marketing", label: "Cleaning marketing in Denver", description: "Compete in Denver's residential and commercial janitorial market — neighborhood by neighborhood." },
        { to: "/castle-rock-digital-marketing", label: "Cleaning marketing in Castle Rock", description: "High-ticket residential and HOA recurring cleaning across Castle Rock and Castle Pines." },
      ]}
    />
  ),
});
